'use client';

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from '../lib/theme';
import TopLoadingBar from '@/components/TopLoadingBar';
import { useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserProvider } from '@/contexts/UserContext';
import { ContextManagerProvider } from '@/contexts';
import PushNotificationProvider from '@/components/PushNotificationProvider';
import { ErrorModalProvider } from '@/utils/errorModal';
import GlobalErrorBoundary from '@/components/GlobalErrorBoundary';
import { reportError } from '@/utils/errorReporting';

import './globals.css';
import '../styles/tooltip.css';
import { Poppins } from 'next/font/google';

const poppins = Poppins({
  weight: ['400', '700'],
  style: ['normal', 'italic'],
  subsets: ['latin'],
  display: 'swap',
});

const toError = (value: unknown): Error => {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  if (value && typeof value === 'object') {
    const anyValue = value as any;
    const message =
      anyValue?.message ||
      anyValue?.error ||
      anyValue?.details?.message ||
      anyValue?.details?.error_message ||
      '';
    if (message) return new Error(String(message));
    try {
      return new Error(JSON.stringify(value));
    } catch {
      return new Error('Unknown object error');
    }
  }
  return new Error(String(value));
};

export default function RootLayout({ children }: { children: React.ReactNode }) {
  // Global network status hook
  useEffect(() => {
    function handleOffline() {
      toast.error("You are offline. Please check your internet connection.", { toastId: 'offline', autoClose: false });
    }
    function handleOnline() {
      // Use safe dismiss to prevent removalReason errors
      try {
        toast.dismiss('offline');
      } catch (error) {
        // Silently handle - toast may already be removed
        console.warn('Error dismissing offline toast:', error);
      }
      toast.success("You are back online!", { toastId: 'online', autoClose: 2000 });
    }
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Global error handlers for unhandled errors (async, event handlers, etc.)
  useEffect(() => {
    // Handle unhandled promise rejections
    function handleUnhandledRejection(event: PromiseRejectionEvent) {
      console.error('Unhandled Promise Rejection:', event.reason);
      const error = toError(event.reason);
      
      reportError({
        error,
        context: 'Unhandled Promise Rejection',
        showToast: false,
      }).catch((reportingError) => {
        console.error('Failed to report unhandled rejection:', reportingError);
      });
    }

    // Handle global errors (window.onerror)
    function handleError(event: ErrorEvent) {
      console.error('Global Error:', event.error || event.message);
      const error = event.error instanceof Error
        ? event.error
        : toError(event.message || 'Unknown error');
      
      reportError({
        error,
        context: 'Global Window Error',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
        showToast: false,
      }).catch((reportingError) => {
        console.error('Failed to report global error:', reportingError);
      });
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <html lang="en" className={poppins.className}>
      <body suppressHydrationWarning>
        <GlobalErrorBoundary>
          <div className="app-layout-wrapper">
            <TopLoadingBar />
            <ThemeProvider theme={theme}>
              <UserProvider>
                <ContextManagerProvider>
                  <ErrorModalProvider>
                    <PushNotificationProvider autoInitialize={true}>
                      {children}
                      <ToastContainer
                        position="top-right"
                        autoClose={5000}
                        hideProgressBar={false}
                        newestOnTop={false}
                        closeOnClick={false}
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        theme="light"
                        limit={5}
                      />
                    </PushNotificationProvider>
                  </ErrorModalProvider>
                </ContextManagerProvider>
              </UserProvider>
            </ThemeProvider>
          </div>
        </GlobalErrorBoundary>
      </body>
    </html>
  );
}
